import { useNavigate, useSearchParams, Link } from 'react-router';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordRequirementsSection } from './PasswordRequirementsSection';
import { AuthBrandLogo } from './AuthBrandLogo';
import { PasswordInput } from '@/components';
import { toast } from 'sonner';
import {
  resetPasswordConfirmSchema,
  type ResetPasswordConfirmFormData,
} from '../types';
import { useConfirmResetPassword } from '../hooks/authApi';
import { usePageTitle } from '@/hooks';

export const ResetPasswordConfirm = () => {
  usePageTitle('Reset Password');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync, isPending, error } = useConfirmResetPassword();

  const form = useForm<ResetPasswordConfirmFormData>({
    resolver: zodResolver(resetPasswordConfirmSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = form.watch('password');

  const onSubmit = async (data: ResetPasswordConfirmFormData) => {
    const email = searchParams.get('email');
    const code = data.code;

    if (!code || !email) {
      toast.error('Invalid reset password link or missing confirmation code');
      return;
    }

    try {
      await mutateAsync({
        email,
        code,
        password: data.password,
      });
      toast.success('Password was reset.');
      navigate('/login');
    } catch {
      toast.error('Failed to reset password.');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md px-4 py-8">
        <div className="mb-8 flex justify-center">
          <AuthBrandLogo className="h-12" />
        </div>

        <Card className="px-8 py-10">
          <h1 className="mb-8 text-center text-2xl font-semibold text-gray-900">
            Reset Password
          </h1>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-4">
                <FormItem>
                  <FormLabel>Confirmation Code</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      autoComplete="off"
                      className={
                        form.formState.errors.code ? 'border-destructive' : ''
                      }
                      aria-invalid={!!form.formState.errors.code}
                      aria-describedby={
                        form.formState.errors.code ? 'code-error' : undefined
                      }
                      {...form.register('code', {
                        required: 'Confirmation code is required',
                      })}
                    />
                  </FormControl>
                  {form.formState.errors.code && (
                    <FormMessage
                      id="code-error"
                      className="text-destructive mt-1 text-sm"
                      role="alert"
                    >
                      {form.formState.errors.code.message}
                    </FormMessage>
                  )}
                </FormItem>

                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="password-input"
                      autoComplete="new-password"
                      className={
                        form.formState.errors.password
                          ? 'border-destructive'
                          : ''
                      }
                      aria-invalid={!!form.formState.errors.password}
                      aria-describedby={
                        form.formState.errors.password
                          ? 'password-error'
                          : undefined
                      }
                      {...form.register('password')}
                    />
                  </FormControl>
                </FormItem>

                <PasswordRequirementsSection password={password} />

                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      data-testid="confirm-password-input"
                      autoComplete="new-password"
                      className={
                        form.formState.errors.confirmPassword
                          ? 'border-destructive'
                          : ''
                      }
                      aria-invalid={!!form.formState.errors.confirmPassword}
                      aria-describedby={
                        form.formState.errors.confirmPassword
                          ? 'confirm-password-error'
                          : undefined
                      }
                      {...form.register('confirmPassword')}
                      showPasswordLabel="Show confirm password"
                      hidePasswordLabel="Hide confirm password"
                    />
                  </FormControl>
                  {form.formState.errors.confirmPassword && (
                    <FormMessage
                      id="confirm-password-error"
                      className="text-destructive mt-1 text-sm"
                      role="alert"
                    >
                      {form.formState.errors.confirmPassword.message}
                    </FormMessage>
                  )}
                </FormItem>
              </div>

              {error && (
                <div
                  className="text-destructive text-center text-sm"
                  role="alert"
                >
                  {error.message}
                </div>
              )}

              <Button
                type="submit"
                disabled={isPending || !form.formState.isValid}
                className="w-full"
                loading={isPending}
              >
                {isPending ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          </Form>

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-primary-600 hover:text-primary-500 text-sm"
            >
              Back to Login
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};
